import { useToast } from '@components/Common/Toast/ToastProvider';
import { PageHero, Panel } from '@components/Common/Panel';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import FormField from '@components/Common/FormField';
import PageStats from '@components/Common/PageStats';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    fetchRulesRequest,
    toggleStatusRequest,
    updateRuleRequest,
    type AutomatedRuleRecord,
} from '@store/features/automatedEmails/actions';
import type { RootState } from '@store/rootReducer';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './index.scss';
import { RULES_FETCH_SIZE } from './audience';
import { emailAudienceLabel, ruleAudienceOptions } from '@helpers/emailAudience';
import StatTile from '@components/Common/StatTile';
import { TEMPLATE_KEY_FORMAT } from '@components/EmailTemplates/types';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';

type RuleStatus = 'ACTIVE' | 'PAUSED';
type TriggerType = 'EVENT' | 'CONDITION' | 'RECURRING';
type ScheduleType = 'IMMEDIATE' | 'DELAYED' | 'WINDOW';
type RulePriority = 'HIGH' | 'NORMAL' | 'LOW';

interface AutomatedRule {
  id: string;
  name: string;
  audience: string;
  triggerType: TriggerType;
  scheduleType: ScheduleType;
  templateKey: string;
  priority: RulePriority;
  nextRun: string;
  status: RuleStatus;
  lastEnqueueResult: 'SUCCESS' | 'SKIPPED' | 'FAILED' | 'NEW';
}

interface RuleHistoryEvent {
  id: string;
  title: string;
  timestamp: string;
  status: 'SUCCESS' | 'WARNING' | 'ERROR' | 'INFO';
  description: string;
}

// Rules are seeded in MongoDB by AutomationRuleSeederService and fetched via Redux.
// See: mintly-backend-graphql-service/src/main/java/com/mintly/service/AutomationRuleSeederService.java

const statusLabelMap: Record<RuleStatus, string> = {
  ACTIVE: 'Active',
  PAUSED: 'Paused',
};

const statusSubtextMap: Record<RuleStatus, string> = {
  ACTIVE: 'Rule is evaluating',
  PAUSED: 'Manually paused',
};

const enqueueLabelMap: Record<AutomatedRule['lastEnqueueResult'], string> = {
  SUCCESS: 'Success',
  SKIPPED: 'Skipped',
  FAILED: 'Failed',
  NEW: 'Never run',
};

const enqueueSubtextMap: Record<AutomatedRule['lastEnqueueResult'], string> = {
  SUCCESS: 'Recipients queued',
  SKIPPED: 'No eligible users',
  FAILED: 'Needs attention',
  NEW: 'Waiting for first trigger',
};

const triggerOptions = [
  { value: 'EVENT', label: 'Event-based' },
  { value: 'CONDITION', label: 'Condition-based' },
  { value: 'RECURRING', label: 'Recurring' },
];

const scheduleOptions = [
  { value: 'IMMEDIATE', label: 'Immediate enqueue' },
  { value: 'DELAYED', label: 'Delayed window' },
  { value: 'WINDOW', label: 'Recurring window' },
];

const priorityOptions = [
  { value: 'HIGH', label: 'High' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'LOW', label: 'Low' },
];

const templateOptions = [
  // Jobseeker templates
  { value: 'js-welcome', label: 'js-welcome' },
  { value: 'js-profile-incomplete', label: 'js-profile-incomplete' },
  { value: 'js-resume-not-uploaded', label: 'js-resume-not-uploaded' },
  { value: 'js-first-application', label: 'js-first-application' },
  { value: 'js-inactivity-reengagement', label: 'js-inactivity-reengagement' },
  { value: 'js-course-upsell', label: 'js-course-upsell' },
  { value: 'js-job-alert', label: 'js-job-alert' },
  { value: 'js-interview-prep', label: 'js-interview-prep' },
  { value: 'js-application-dropoff', label: 'js-application-dropoff' },
  { value: 'js-success-story', label: 'js-success-story' },
  { value: 'js-whatsapp-optin', label: 'js-whatsapp-optin' },
  { value: 'js-weekly-jobs-digest', label: 'js-weekly-jobs-digest' },
  { value: 'js-referral', label: 'js-referral' },
  { value: 'js-cert-completion', label: 'js-cert-completion' },
  // Employer templates
  { value: 'em-welcome', label: 'em-welcome' },
  { value: 'em-job-posting-incomplete', label: 'em-job-posting-incomplete' },
  { value: 'em-no-job-posted', label: 'em-no-job-posted' },
  { value: 'em-candidate-match', label: 'em-candidate-match' },
  { value: 'em-reengagement', label: 'em-reengagement' },
  { value: 'em-upgrade-unlock-profiles', label: 'em-upgrade-unlock-profiles' },
  { value: 'em-upgrade-multiple-views', label: 'em-upgrade-multiple-views' },
  { value: 'em-upgrade-view-limit', label: 'em-upgrade-view-limit' },
  { value: 'em-upgrade-high-intent', label: 'em-upgrade-high-intent' },
  { value: 'em-trial-ending', label: 'em-trial-ending' },
  { value: 'em-candidate-matches-job', label: 'em-candidate-matches-job' },
  { value: 'em-upgrade-repeated-view', label: 'em-upgrade-repeated-view' },
  { value: 'em-upgrade-plan-limit', label: 'em-upgrade-plan-limit' },
  { value: 'em-upgrade-growth', label: 'em-upgrade-growth' },
  { value: 'em-fomo-hiring', label: 'em-fomo-hiring' },
  { value: 'em-interview-tips', label: 'em-interview-tips' },
  { value: 'em-company-profile-incomplete', label: 'em-company-profile-incomplete' },
  { value: 'em-weekly-candidates', label: 'em-weekly-candidates' },
  { value: 'em-monthly-insights', label: 'em-monthly-insights' },
  { value: 'em-refill-hiring', label: 'em-refill-hiring' },
  { value: 'em-application-digest', label: 'em-application-digest' },
  { value: 'em-success-story', label: 'em-success-story' },
  { value: 'em-job-expiry', label: 'em-job-expiry' },
  { value: 'em-recruiter-education', label: 'em-recruiter-education' },
  { value: 'em-subscription-renewal', label: 'em-subscription-renewal' },
  { value: 'em-whatsapp-alerts', label: 'em-whatsapp-alerts' },
];

const historyEvents: RuleHistoryEvent[] = [
  {
    id: 'hist-1',
    title: 'Rule saved',
    timestamp: '06 Jun 2026, 09:45 AM',
    status: 'SUCCESS',
    description: 'Immediate enqueue enabled and audience targeting updated.',
  },
  {
    id: 'hist-2',
    title: 'Evaluator run completed',
    timestamp: '06 Jun 2026, 08:15 AM',
    status: 'INFO',
    description: '2 recipients enqueued, 1 skipped by frequency cap.',
  },
  {
    id: 'hist-3',
    title: 'Scheduled window paused',
    timestamp: '05 Jun 2026, 05:30 PM',
    status: 'WARNING',
    description: 'Delivery paused during quiet hours for employer audience.',
  },
  {
    id: 'hist-4',
    title: 'Template sync failed',
    timestamp: '05 Jun 2026, 02:10 PM',
    status: 'ERROR',
    description: 'Template version mismatch prevented queue creation for one rule.',
  },
];

/** Map Redux rule record to the local AutomatedRule shape. */
const toAutoRule = (r: AutomatedRuleRecord): AutomatedRule => ({
  id: r.id,
  name: r.name,
  audience: emailAudienceLabel(r.audience),
  triggerType: r.triggerType as TriggerType,
  scheduleType: r.scheduleType as ScheduleType,
  templateKey: r.templateKey,
  priority: r.priority as RulePriority,
  nextRun: r.nextRun ?? '',
  status: r.status as RuleStatus,
  lastEnqueueResult: (r.lastEnqueueResult ?? 'NEW') as AutomatedRule['lastEnqueueResult'],
});

const AutomatedEmails: React.FC = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { rules: reduxRules, loading: reduxLoading } = useSelector(
    (state: RootState) => state.automatedEmails,
  );
  const rules: AutomatedRule[] = useMemo(() => reduxRules.map(toAutoRule), [reduxRules]);
  const audienceOptions = useMemo(
    () => ruleAudienceOptions(rules.map((r) => r.audience)),
    [rules],
  );
  const [audienceFilter, setAudienceFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'CREATE' | 'EDIT'>('CREATE');
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRule, setHistoryRule] = useState<AutomatedRule | null>(null);
  const [selectedRule, setSelectedRule] = useState<AutomatedRule | null>(null);
  const [sampleEmailOpen, setSampleEmailOpen] = useState(false);
  const [sampleEmailRule, setSampleEmailRule] = useState<AutomatedRule | null>(null);
  const [sampleEmailAddress, setSampleEmailAddress] = useState('');
  const [sampleEmailSent, setSampleEmailSent] = useState(false);
  const [sampleEmailLoading, setSampleEmailLoading] = useState(false);
  const [sampleEmailError, setSampleEmailError] = useState('');
  const [globalPauseLoading, setGlobalPauseLoading] = useState(false);
  const [confirmPause, setConfirmPause] = useState(false);
  const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);
  const [rowMenuRule, setRowMenuRule] = useState<AutomatedRule | null>(null);

  // Fetch rules from API on mount
  useEffect(() => {
    dispatch(fetchRulesRequest(0, RULES_FETCH_SIZE));
  }, [dispatch]);

  const handleGlobalPause = async () => {
    setConfirmPause(false);
    setGlobalPauseLoading(true);
    try {
      const activeRuleIds = rules.filter(r => r.status === 'ACTIVE').map(r => r.id);
      await Promise.all(activeRuleIds.map(id => axios.post(`/api/automation-rules/${id}/pause`)));
      dispatch(fetchRulesRequest(0, RULES_FETCH_SIZE));
    } catch {
      // silent — individual pause errors don't need a modal
    } finally {
      setGlobalPauseLoading(false);
    }
  };

  const openRowMenu = (event: React.MouseEvent<HTMLButtonElement>, rule: AutomatedRule) => {
    event.stopPropagation();
    setRowMenuAnchor(event.currentTarget);
    setRowMenuRule(rule);
  };

  const closeRowMenu = () => {
    setRowMenuAnchor(null);
    setRowMenuRule(null);
  };

  const [draftRule, setDraftRule] = useState<AutomatedRule>({
    id: 'new-rule',
    name: '',
    audience: 'Jobseekers',
    triggerType: 'EVENT',
    scheduleType: 'IMMEDIATE',
    templateKey: 'js-welcome',
    priority: 'NORMAL',
    nextRun: 'On signup event',
    status: 'ACTIVE',
    lastEnqueueResult: 'SUCCESS',
  });

  const filteredRules = useMemo(() => {
    return rules.filter((rule) => {
      const audienceMatch = audienceFilter === 'ALL' || rule.audience === audienceFilter;
      const statusMatch = statusFilter === 'ALL' || rule.status === statusFilter;
      return audienceMatch && statusMatch;
    });
  }, [rules, audienceFilter, statusFilter]);

  const activeRules = rules.filter((rule) => rule.status === 'ACTIVE').length;
  const failedEnqueues = rules.filter((rule) => rule.lastEnqueueResult === 'FAILED').length;
  const immediateRules = rules.filter((rule) => rule.scheduleType === 'IMMEDIATE').length;
  const scheduledRules = rules.filter((rule) => rule.scheduleType !== 'IMMEDIATE').length;
  const hasActiveFilters = audienceFilter !== 'ALL' || statusFilter !== 'ALL';

  const openCreateEditor = () => {
    setEditorMode('CREATE');
    setSelectedRuleId(null);
    setDraftRule({
      id: 'new-rule',
      name: '',
      audience: 'Jobseekers',
      triggerType: 'EVENT',
      scheduleType: 'IMMEDIATE',
      templateKey: 'js-welcome',
      priority: 'NORMAL',
      nextRun: 'On signup event',
      status: 'ACTIVE',
      lastEnqueueResult: 'SUCCESS',
    });
    setEditorOpen(true);
  };

  const openEditEditor = (rule: AutomatedRule) => {
    setEditorMode('EDIT');
    setSelectedRuleId(rule.id);
    setDraftRule(rule);
    setEditorOpen(true);
  };

  const handleRowClick = (rule: AutomatedRule) => {
    setSelectedRule(rule);
  };

  const openHistory = (rule: AutomatedRule) => {
    setHistoryRule(rule);
    setHistoryOpen(true);
  };

  const updateDraft = (name: string, value: any) => {
    setDraftRule((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const toggleRuleStatus = (ruleId: string) => {
    const rule = rules.find((r) => r.id === ruleId);
    if (rule) dispatch(toggleStatusRequest(ruleId, rule.status));
  };

  const duplicateRule = (rule: AutomatedRule) => {
    // Optimistic local duplicate — persisted when user saves via editor
    dispatch(updateRuleRequest(rule.id, { name: `${rule.name} (Copy)` }));
  };

  const resetFilters = () => {
    setAudienceFilter('ALL');
    setStatusFilter('ALL');
  };

  const isSelectedRule = (ruleId: string) => selectedRule?.id === ruleId;

  const openSampleEmail = (rule: AutomatedRule) => {
    setSampleEmailRule(rule);
    setSampleEmailAddress('');
    setSampleEmailSent(false);
    setSampleEmailError('');
    setSampleEmailOpen(true);
  };

  const sendSampleEmail = async () => {
    if (!sampleEmailRule) return;
    setSampleEmailLoading(true);
    setSampleEmailError('');
    try {
      await axios.post(`/api/automation-rules/${sampleEmailRule.id}/sample-send`, { toEmail: sampleEmailAddress });
      setSampleEmailSent(true);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to send sample email';
      setSampleEmailError(msg);
    } finally {
      setSampleEmailLoading(false);
    }
  };

  const saveRule = () => {
    /*
     * Rule name and template both carry an asterisk. The name was guarded by a disabled button,
     * the template by nothing at all, so a rule could be saved pointing at no template and stop
     * sending with no indication of why.
     */
    if (!draftRule.name.trim() || !draftRule.templateKey) {
      showToast(
        !draftRule.name.trim() ? 'A rule name is required' : 'Choose an email template',
        'error',
      );
      document.getElementById(!draftRule.name.trim() ? 'name' : 'templateKey')?.focus();
      return;
    }
    // The backend refuses the same thing; saying so here keeps the form open with the reason.
    if (!TEMPLATE_KEY_FORMAT.test(draftRule.templateKey)) {
      showToast('Template keys are lowercase words joined by hyphens, for example js-welcome', 'error');
      document.getElementById('templateKey')?.focus();
      return;
    }
    if (editorMode === 'EDIT' && selectedRuleId) {
      dispatch(updateRuleRequest(selectedRuleId, {
        name: draftRule.name,
        priority: draftRule.priority,
        nextRun: draftRule.nextRun,
        templateKey: draftRule.templateKey,
      }));
    }
    // CREATE mode: would need a createAutomationRule mutation — not yet wired
    setEditorOpen(false);
  };

  const tableSelectors = ['name', 'audience', 'triggerType', 'scheduleType', 'status', 'lastEnqueueResult', 'actions'];

  const tableHeadRow = {
    name: 'Rule Name',
    audience: 'Audience',
    triggerType: 'Trigger',
    scheduleType: 'Schedule',
    status: 'Status',
    lastEnqueueResult: 'Last Enqueue',
    actions: 'Actions',
  };

  const tableBodyRow = {
    name: (rule: AutomatedRule) => (
      <Box className={`ae-rule-name-cell${isSelectedRule(rule.id) ? ' is-selected' : ''}`}>
        <Typography className="ae-rule-name">{rule.name}</Typography>
        <Typography className="ae-rule-subtitle" variant="caption">
          Priority: {rule.priority}
        </Typography>
      </Box>
    ),
    audience: (rule: AutomatedRule) => <Chip className="ae-chip" label={rule.audience} size="small" variant="outlined" />,
    triggerType: (rule: AutomatedRule) => rule.triggerType,
    scheduleType: (rule: AutomatedRule) => rule.scheduleType,
    status: (rule: AutomatedRule) => (
      <Box className={`ae-status-cell${isSelectedRule(rule.id) ? ' is-selected' : ''}`}>
        <CRMStatusChip status={rule.status} label={statusLabelMap[rule.status]} />
        <Typography className="ae-status-subtext" variant="caption">
          {statusSubtextMap[rule.status]}
        </Typography>
      </Box>
    ),
    lastEnqueueResult: (rule: AutomatedRule) => (
      <Box className={`ae-enqueue-cell${isSelectedRule(rule.id) ? ' is-selected' : ''}`}>
        {/* The icon is gone with the raw Chip: CRMStatusChip carries no icon slot, and the
            label plus the subtext below already say what the icon repeated. */}
        <CRMStatusChip
          status={rule.lastEnqueueResult}
          label={enqueueLabelMap[rule.lastEnqueueResult]}
        />
        <Typography className="ae-enqueue-subtext" variant="caption">
          {enqueueSubtextMap[rule.lastEnqueueResult]}
        </Typography>
      </Box>
    ),
    actions: (rule: AutomatedRule) => (
      <Box className="ae-row-actions">
        <Tooltip title="Actions">
          <IconButton
            size="small"
            aria-label="Row actions"
            onClick={(event) => openRowMenu(event, rule)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  };

  return (
    <Box className="automated-emails-page">
      <Box className="ae-page-body max-w-container">
        <PageHero
          kicker="Automation control plane"
          title="Schedule, target, and inspect every automated email from one workspace."
          text="Keep templates content-only. Control audience, trigger timing, quiet hours, and delivery rules here."
          actions={(
            <>
              <Button
                variant="outlined"
                startIcon={globalPauseLoading ? <CircularProgress size={14} color="inherit" /> : <PauseCircleOutlineIcon />}
                onClick={() => setConfirmPause(true)}
                disabled={globalPauseLoading || rules.filter(r => r.status === 'ACTIVE').length === 0}
              >
                Global Pause
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateEditor}>
                Create Rule
              </Button>
            </>
          )}
        />

        {/* The shared tile; only a failure takes a colour. */}
        <Box className="cc-stat-grid">
          <StatTile label="Active rules" value={activeRules} />
          <StatTile label="Immediate enqueue" value={immediateRules} />
          <StatTile label="Scheduled windows" value={scheduledRules} />
          <StatTile
            label="Failed enqueues"
            value={failedEnqueues}
            icon={failedEnqueues ? <ErrorOutlineIcon /> : undefined}
            tone={failedEnqueues ? 'danger' : 'neutral'}
          />
        </Box>

        <Box className="ae-workspace">
          <Card variant="outlined" className="cc-panel cc-panel--panel ae-table-panel">
            <CardContent className="ae-table-panel-content">
              <Box className="ae-toolbar">
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 4 }}>
              <SelectFormField
                required={false}
                name="audienceFilter"
                label="Audience filter"
                value={audienceFilter}
                options={[{ value: 'ALL', label: 'All audiences' }, ...audienceOptions]}
                onChange={(_, value) => setAudienceFilter(String(value))}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SelectFormField
                required={false}
                name="statusFilter"
                label="Status filter"
                value={statusFilter}
                options={[
                  { value: 'ALL', label: 'All status' },
                  { value: 'ACTIVE', label: 'Active' },
                  { value: 'PAUSED', label: 'Paused' },
                ]}
                onChange={(_, value) => setStatusFilter(String(value))}
                size="small"
              />
            </Grid>
            {/* No alignItems in either sx. `.ae-toolbar-actions` already sets `flex-end` in the
                stylesheet, and Emotion injects sx at runtime so `center` here landed last and
                silently beat it — the button sat above the input line of the labelled selects in
                the sibling cells. */}
            <Grid size={{ xs: 12, md: 4 }} className="ae-toolbar-cell">
              <Stack direction="row" spacing={1} className="ae-toolbar-actions">
                {/* A text button, not a link dressed as one. It used to zero
                    its own padding and drop to weight 400 with an underline,
                    which made it the only 400-weight button in the console. */}
                <Button
                  variant="text"
                  onClick={resetFilters}
                  disabled={!hasActiveFilters}
                  size="small"
                >
                  Reset filters
                </Button>
                {hasActiveFilters && <Chip size="small" label="Filters active" color="primary" variant="outlined" />}
              </Stack>
            </Grid>
          </Grid>
              </Box>

              <Box className="ae-table-section">
                <PageStats total={rules.length} filtered={filteredRules.length} label="rules" />
                {reduxLoading ? (
                  <SkeletonLoader rows={8} columns={tableSelectors.length} />
                ) : (
                  <MintlyTable
                    rows={filteredRules}
                    selectors={tableSelectors}
                    tableHeadRow={tableHeadRow}
                    tableBodyRow={tableBodyRow}
                    onRowClick={handleRowClick}
                    pagination={{
                      enabled: false,
                      page: 0,
                      rowsPerPage: 10,
                      totalNumberOfRows: filteredRules.length,
                      handleChangePage: () => {},
                    }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>

          <Box className="ae-inspector-column">
            {selectedRule ? (
              <>
                <Card variant="outlined" className="cc-panel cc-panel--panel ae-inspector-card">
                  <CardContent>
                    <Typography className="ae-inspector-label" variant="overline">
                      Selected rule
                    </Typography>
                    <Typography className="ae-inspector-title" variant="h6">
                      {selectedRule.name}
                    </Typography>
                    <Typography className="ae-inspector-meta" variant="body2">
                      {selectedRule.audience} · {selectedRule.triggerType} · {selectedRule.scheduleType}
                    </Typography>

                    <Divider className="ae-inspector-divider" />

                    <Stack spacing={1.25}>
                      <Panel variant="stat" className="ae-inspector-metric">
                        <Typography className="ae-inspector-metric-label" variant="caption">
                          Template
                        </Typography>
                        <Typography className="ae-inspector-metric-value" variant="body2">
                          {selectedRule.templateKey}
                        </Typography>
                      </Panel>
                      <Panel variant="stat" className="ae-inspector-metric">
                        <Typography className="ae-inspector-metric-label" variant="caption">
                          Priority
                        </Typography>
                        <Typography className="ae-inspector-metric-value" variant="body2">
                          {selectedRule.priority}
                        </Typography>
                      </Panel>
                      <Panel variant="stat" className="ae-inspector-metric">
                        <Typography className="ae-inspector-metric-label" variant="caption">
                          Next run
                        </Typography>
                        <Typography className="ae-inspector-metric-value" variant="body2">
                          {selectedRule.nextRun}
                        </Typography>
                      </Panel>
                    </Stack>

                    <Box className="ae-inspector-progress-block">
                      <Typography className="ae-inspector-metric-label" variant="caption">
                        Delivery confidence
                      </Typography>
                      <LinearProgress
                        className="ae-inspector-progress"
                        variant="determinate"
                        value={selectedRule.lastEnqueueResult === 'SUCCESS' ? 92 : selectedRule.lastEnqueueResult === 'SKIPPED' ? 68 : 34}
                      />
                    </Box>

                    <Stack direction="row" spacing={1} className="ae-inspector-actions">
                      <Button size="small" variant="outlined" onClick={() => openHistory(selectedRule)}>
                        History
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => openEditEditor(selectedRule)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SendOutlinedIcon />}
                        onClick={() => openSampleEmail(selectedRule)}
                        className="ae-sample-btn"
                      >
                        Send sample
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined" className="cc-panel cc-panel--panel ae-inspector-card">
                  <CardContent>
                    <Typography className="ae-inspector-label" variant="overline">
                      Recent events
                    </Typography>
                    <Box className="ae-activity-list">
                      {historyEvents.slice(0, 3).map((event) => (
                        <Box key={event.id} className={`ae-activity-item ae-activity-item--${event.status.toLowerCase()}`}>
                          <Box className="ae-history-item-badge" />
                          <Box className="ae-activity-copy">
                            <Typography className="ae-activity-title" variant="body2">
                              {event.title}
                            </Typography>
                            <Typography className="ae-activity-text" variant="caption">
                              {event.description}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card variant="outlined" className="cc-panel cc-panel--panel ae-inspector-card ae-inspector-card--empty">
                <CardContent>
                  <Typography className="ae-inspector-label" variant="overline">
                    Rule details
                  </Typography>
                  <EmptyState
                    compact
                    title="Select a rule to inspect it"
                    body="This panel stays empty until a row is chosen, so the table remains the primary workspace."
                  />
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        className="ae-editor-dialog"
        slotProps={{
          paper: {
            sx: {
              width: { xs: 'calc(100% - 24px)', sm: '100%' },
              maxWidth: { xs: 'calc(100% - 24px)', sm: 560 },
              mx: { xs: '12px', sm: 'auto' },
              my: { xs: '12px', sm: 'auto' },
              maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
            },
          },
        }}
      >
        <DialogTitle className="ae-editor-title">
          <Typography variant="h6" component="span">
            {editorMode === 'CREATE' ? 'Create automated rule' : 'Edit automated rule'}
          </Typography>
          <Typography variant="body2" color="text.secondary" className="ae-editor-subtitle">
            {editorMode === 'CREATE'
              ? 'Configure targeting, trigger, and delivery settings.'
              : `Editing: ${draftRule.name || 'rule'}`}
          </Typography>
        </DialogTitle>

        <DialogContent dividers className="ae-editor-content" sx={{ p: 0, overflowX: 'hidden' }}>
          {/* Identity */}
          <Box className="ae-form-section">
            <Typography className="ae-form-section-label">Rule identity</Typography>
            <Stack spacing={2}>
              <FormField
                required
                name="name"
                label="Rule name"
                value={draftRule.name}
                onChange={updateDraft}
                placeholder="e.g. New jobseeker welcome"
                size="small"
              />
              <SelectFormField
                required={false}
                name="audience"
                label="Audience"
                value={draftRule.audience}
                options={audienceOptions}
                onChange={updateDraft}
                size="small"
              />
            </Stack>
          </Box>

          <Divider className="ae-form-divider" />

          {/* Trigger & Scheduling */}
          <Box className="ae-form-section">
            <Typography className="ae-form-section-label">Trigger &amp; scheduling</Typography>
            <Stack spacing={2}>
              <SelectFormField
                required={false}
                name="triggerType"
                label="Trigger type"
                value={draftRule.triggerType}
                options={triggerOptions}
                onChange={updateDraft}
                size="small"
              />
              <SelectFormField
                required={false}
                name="scheduleType"
                label="Schedule type"
                value={draftRule.scheduleType}
                options={scheduleOptions}
                onChange={updateDraft}
                size="small"
              />
            </Stack>
          </Box>

          <Divider className="ae-form-divider" />

          {/* Delivery */}
          <Box className="ae-form-section">
            <Typography className="ae-form-section-label">Delivery</Typography>
            <Stack spacing={2}>
              <SelectFormField
                required
                name="templateKey"
                label="Email template"
                value={draftRule.templateKey}
                options={templateOptions}
                onChange={updateDraft}
                size="small"
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <SelectFormField
                  required={false}
                  name="priority"
                  label="Priority"
                  value={draftRule.priority}
                  options={priorityOptions}
                  onChange={updateDraft}
                  size="small"
                />
                <SelectFormField
                  required={false}
                  name="status"
                  label="Status"
                  value={draftRule.status}
                  options={[
                    { value: 'ACTIVE', label: 'Active' },
                    { value: 'PAUSED', label: 'Paused' },
                  ]}
                  onChange={updateDraft}
                  size="small"
                />
              </Stack>
            </Stack>
          </Box>

          <Divider className="ae-form-divider" />

          {/* Timing */}
          <Box className="ae-form-section ae-form-section--last">
            <Typography className="ae-form-section-label">Timing</Typography>
            <Stack spacing={2}>
              <FormField
                required={false}
                name="nextRun"
                label="Next run / window"
                value={draftRule.nextRun}
                onChange={updateDraft}
                placeholder="e.g. Tomorrow at 10:00 AM"
                size="small"
              />
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions className="ae-editor-actions">
          <Button onClick={() => setEditorOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={saveRule} disabled={!draftRule.name.trim()}>
            {editorMode === 'CREATE' ? 'Create rule' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Row actions overflow menu ──────────────────────────── */}
      <Menu
        anchorEl={rowMenuAnchor}
        open={Boolean(rowMenuAnchor)}
        onClose={closeRowMenu}
        onClick={closeRowMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        <MenuItem onClick={() => { openEditEditor(rowMenuRule!); }}>
          <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit rule</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { openSampleEmail(rowMenuRule!); }}>
          <ListItemIcon><SendOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Send sample email</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { openHistory(rowMenuRule!); }}>
          <ListItemIcon><HistoryOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View run history</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { duplicateRule(rowMenuRule!); }}>
          <ListItemIcon><FileCopyOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Duplicate rule</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => { toggleRuleStatus(rowMenuRule!.id); }}
          sx={{ color: rowMenuRule?.status === 'ACTIVE' ? 'warning.main' : 'success.main' }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            {rowMenuRule?.status === 'ACTIVE'
              ? <PauseCircleOutlineIcon fontSize="small" />
              : <PlayArrowIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{rowMenuRule?.status === 'ACTIVE' ? 'Pause rule' : 'Resume rule'}</ListItemText>
        </MenuItem>
      </Menu>

      {/* ── Send Sample Email Dialog ───────────────────────────── */}
      <Dialog
        open={sampleEmailOpen}
        onClose={() => setSampleEmailOpen(false)}
        fullWidth
        maxWidth="xs"
        scroll="paper"
        className="ae-sample-dialog"
        slotProps={{
          paper: {
            sx: {
              width: { xs: 'calc(100% - 24px)', sm: '100%' },
              maxWidth: { xs: 'calc(100% - 24px)', sm: 440 },
              mx: { xs: '12px', sm: 'auto' },
              my: { xs: '12px', sm: 'auto' },
            },
          },
        }}
      >
        <DialogTitle className="ae-editor-title">
          <Typography variant="h6" component="span">
            Send sample email
          </Typography>
          <Typography variant="body2" color="text.secondary" className="ae-editor-subtitle">
            {sampleEmailRule?.name ?? 'Selected rule'}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {sampleEmailSent ? (
            <Box className="ae-sample-success">
              <CheckCircleOutlineIcon className="ae-sample-success-icon" />
              <Typography variant="h6">Sample sent</Typography>
              <Typography variant="body2" color="text.secondary">
                Check <strong>{sampleEmailAddress}</strong>. Delivery may take a few minutes.
              </Typography>
            </Box>
          ) : (
            <Box className="ae-form-section ae-form-section--last">
              <Box className="ae-sample-meta">
                <Box className="ae-sample-meta-row">
                  <Typography variant="caption" className="ae-sample-meta-label">Template</Typography>
                  <Typography variant="body2" className="ae-sample-meta-value">{sampleEmailRule?.templateKey}</Typography>
                </Box>
                <Box className="ae-sample-meta-row">
                  <Typography variant="caption" className="ae-sample-meta-label">Audience</Typography>
                  <Typography variant="body2" className="ae-sample-meta-value">{sampleEmailRule?.audience}</Typography>
                </Box>
                <Box className="ae-sample-meta-row">
                  <Typography variant="caption" className="ae-sample-meta-label">Trigger</Typography>
                  <Typography variant="body2" className="ae-sample-meta-value">{sampleEmailRule?.triggerType}</Typography>
                </Box>
              </Box>
              <Divider className="ae-form-divider" sx={{ mx: '-24px', my: 'var(--spacing-lg)' }} />
              <FormField
                required={false}
                name="sampleEmailAddress"
                label="Send to"
                value={sampleEmailAddress}
                onChange={(_name, val) => setSampleEmailAddress(String(val))}
                placeholder="name@company.com"
                type="email"
                size="small"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 'var(--spacing-sm)' }}>
                A test version of this email will be sent using placeholder variables.
              </Typography>
              {sampleEmailError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 'var(--spacing-sm)' }}>
                  {sampleEmailError}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions className="ae-editor-actions">
          <Button onClick={() => setSampleEmailOpen(false)} color="inherit">
            {sampleEmailSent ? 'Close' : 'Cancel'}
          </Button>
          {!sampleEmailSent && (
            <Button
              variant="contained"
              startIcon={sampleEmailLoading ? <CircularProgress size={14} color="inherit" /> : <SendOutlinedIcon />}
              onClick={sendSampleEmail}
              disabled={!sampleEmailAddress.trim() || !sampleEmailAddress.includes('@') || sampleEmailLoading}
            >
              {sampleEmailLoading ? 'Sending…' : 'Send sample'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          <Stack spacing={0.5}>
            <Typography variant="h6">Rule History</Typography>
            <Typography variant="body2" color="text.secondary">
              {historyRule?.name ?? 'Selected rule'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box className="ae-history-summary">
            <Box className="ae-history-summary-card">
              <Typography className="ae-summary-label">Audience</Typography>
              <Typography className="ae-summary-value">{historyRule?.audience ?? '-'}</Typography>
            </Box>
            <Box className="ae-history-summary-card">
              <Typography className="ae-summary-label">Schedule</Typography>
              <Typography className="ae-summary-value">{historyRule?.scheduleType ?? '-'}</Typography>
            </Box>
            <Box className="ae-history-summary-card">
              <Typography className="ae-summary-label">Template</Typography>
              <Typography className="ae-summary-value">{historyRule?.templateKey ?? '-'}</Typography>
            </Box>
          </Box>

          <Box className="ae-history-list">
            {historyEvents.map((event) => (
              <Box key={event.id} className={`ae-history-item ae-history-item--${event.status.toLowerCase()}`}>
                <Box className="ae-history-item-badge" />
                <Box className="ae-history-item-content">
                  <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography className="ae-history-title">{event.title}</Typography>
                      <Typography className="ae-history-description" variant="body2">
                        {event.description}
                      </Typography>
                    </Box>
                    <Typography className="ae-history-time" variant="caption" sx={{ flexShrink: 0 }}>
                      {event.timestamp}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* The most destructive control on the page, and it was behind `window.confirm`: one OK
          button, unstyled, and dismissible by pressing Enter on a page you were already typing
          on. `confirmText` makes it deliberate. */}
      <ConfirmDialog
        open={confirmPause}
        title="Pause every active rule?"
        body={
          <>
            All {rules.filter((r) => r.status === 'ACTIVE').length} active rules stop triggering
            emails. Resuming is one rule at a time, so this is much faster to do than to undo.
          </>
        }
        confirmText="PAUSE ALL"
        confirmLabel="Confirmation"
        actionLabel="Pause all"
        busy={globalPauseLoading}
        onConfirm={handleGlobalPause}
        onCancel={() => setConfirmPause(false)}
      />
    </Box>
  );
};

export default AutomatedEmails;
